import React, { useContext, useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { TextileAppState } from 'api/textile_deals/fetchTextileAppState';
import TopActionBlock from 'blocks/TopActionBlock';
import GenericErrorComponent from 'components/InfoBoxComponent/GenericErrorComponent';
import LoadingIndicatorComponent from 'components/LoadingIndicatorComponent';
import PageStackBlock from 'components/PageStackBlock/PageStackBlock';
import { HasAccessContext } from 'contexts/HasAccessContext';
import BackLink from 'domain/Links/BackLink';
import Headline from 'elements/Headline';
import { useTextileGroupsQuery } from 'queries/textile_deals/useTextileGroupsQueries';
import { useCreateTextileName } from 'queries/textile_deals/useTextileNamesQueries';
import { FormattedMessage } from 'react-intl';
import { HistoryProps } from 'utils/history';
import { textileOrderIdFromMatch } from 'utils/history/textile-order-id-from-match';
import { textileOrderRoute } from 'utils/history/textile-order-route';
import useForm, { FormType, handleFormError } from 'utils/hooks/useForm';
import TextileNameForm from './TextileNameForm';

interface FormProps {
  form: FormType;
}

const AddTeacherTextileName: React.FC<HistoryProps & FormProps> = (props) => {
  const textileOrderId = textileOrderIdFromMatch(props.match);
  const queryClient = useQueryClient();
  const textileAppState = queryClient.getQueryData<TextileAppState>([
    'textile_app_state',
    textileOrderId
  ]);
  const accessContext = useContext(HasAccessContext);
  const textileGroups = useTextileGroupsQuery(textileOrderId);
  const createTeacher = useCreateTextileName(textileOrderId);

  if (!accessContext) {
    throw Error('Component must be used within HasAccessContextProvider');
  }

  useEffect(() => {
    const data = textileGroups.data;
    const textileOrder = textileAppState?.textile_order;

    if (data && textileOrder) {
      props.form.setField('first_name', '');
      if (textileOrder.show_groups) {
        props.form.setField('textile_group_id', data[0]?.id.toString());
      }
    }

    accessContext.checkSectionAccess('design');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessContext, textileGroups.data, textileAppState?.textile_order]);

  const renderPage = (content?: React.ReactNode) => {
    return (
      <>
        <TopActionBlock>
          <BackLink
            to={textileOrderRoute(props.match, '/management/teachers')}
          />
        </TopActionBlock>

        <PageStackBlock>{content}</PageStackBlock>
      </>
    );
  };

  if (textileGroups.isError) {
    return renderPage(
      <GenericErrorComponent onRetryClick={() => textileGroups.refetch()} />
    );
  }

  if (
    !textileAppState ||
    props.form.loading ||
    textileGroups.isLoading ||
    !textileGroups.data
  ) {
    return renderPage(<LoadingIndicatorComponent />);
  }

  const save = () => {
    const { form } = props;
    if (!textileOrderId) {
      return;
    }

    form.setLoading(true);
    form.resetErrors();

    const newTeacher = { ...form.values, role: 'teacher' };

    createTeacher.mutate(
      { textileOrderId, data: newTeacher },
      {
        onSuccess: () => {
          props.history.push(
            textileOrderRoute(props.match, '/management/teachers')
          );
        },
        onError: (error: any) => {
          form.setLoading(false);
          if (!handleFormError(form, error)) {
            form.setError('base', true);
          }
        }
      }
    );
  };

  const renderForm = () => {
    const { form } = props;

    const groups = textileGroups.data;
    const showGroups = textileAppState.textile_order.show_groups;

    return renderPage(
      <>
        <Headline.Large>
          <FormattedMessage id="Add teacher" />
        </Headline.Large>

        <TextileNameForm
          form={form}
          onSave={() => save()}
          textileGroups={groups}
          formOfAddress={true}
          showGroups={showGroups}
        />
      </>
    );
  };

  return renderForm();
};

export default (props: HistoryProps) => {
  const form = useForm();
  // @ts-ignore
  return <AddTeacherTextileName {...props} form={form} />;
};
